import {createSlice} from '@reduxjs/toolkit';

let alertSequence = 0;
function createAlertId() {
    alertSequence += 1;
    return `alert_${Date.now()}_${alertSequence}`;
}

const initialState = {
    isSidebarOpen: window.innerWidth > 768,
    alerts: [],
    dismissedSosDeviceIds: [],
    selectedBeachGeofence: null,
    loading: false,
    shouldNavigateToSelectedDevice: false,
    selectedCity: '',
    selectedState: '',
    selectedCountry: 'in',
    cityBoundary: null, // Cache the GeoJSON boundary for the selected city/state
};

const uiSlice = createSlice({
    name: 'ui',
    initialState,
    reducers: {
        toggleSidebar: (state) => {
            state.isSidebarOpen = !state.isSidebarOpen;
        },
        setSidebarOpen: (state, action) => {
            state.isSidebarOpen = action.payload;
        },
        addAlert: (state, action) => { // Handle both string and object payloads
            const payload = action.payload;
            if (typeof payload === 'object' && payload?.type === 'live_tracking') {
                // Keep exactly one live-tracking alert at a time.
                // Selecting a different LG Band replaces the previous one.
                state.alerts = state.alerts.filter((alert) => alert.type !== 'live_tracking');
                state.alerts.push({
                    id: payload.id || createAlertId(),
                    message: typeof payload.message === 'string' ? payload.message : String(payload.message ?? ''),
                    type: payload.type,
                    trackingKey: payload.trackingKey,
                    deviceStatus: payload.deviceStatus != null ? payload.deviceStatus : undefined,
                    timestamp: payload.timestamp || new Date().toISOString()
                });
                return;
            }
            if (typeof payload === 'object' && payload?.type === 'sos_emergency') {
                state.alerts = state.alerts.filter((alert) => alert.type !== 'sos_emergency');
                state.alerts.push({
                    id: payload.id || createAlertId(),
                    message: typeof payload.message === 'string' ? payload.message : String(payload.message ?? ''),
                    type: payload.type,
                    deviceId: payload.deviceId,
                    lgBandId: payload.lgBandId,
                    firestoreDocId: payload.firestoreDocId,
                    outsideGeofence: payload.outsideGeofence === true,
                    hasPosition: payload.hasPosition !== false,
                    geofenceName: payload.geofenceName || null,
                    sosAt: payload.sosAt || null,
                    nearestTower: payload.nearestTower || null,
                    nearestTowerDistanceM: payload.nearestTowerDistanceM != null ? payload.nearestTowerDistanceM : null,
                    nearestBeach: payload.nearestBeach || null,
                    nearestBeachDistanceM: payload.nearestBeachDistanceM != null ? payload.nearestBeachDistanceM : null,
                    timestamp: payload.timestamp || new Date().toISOString()
                });
                return;
            }
            const newAlert = typeof payload === 'string' ? {
                id: createAlertId(),
                message: payload,
                timestamp: new Date().toISOString()
            } : {
                id: payload.id || createAlertId(),
                message: typeof payload.message === 'string' ? payload.message : String(payload.message ?? ''),
                type: payload.type,
                trackingKey: payload.trackingKey,
                deviceStatus: payload.deviceStatus != null ? payload.deviceStatus : undefined,
                timestamp: payload.timestamp || new Date().toISOString()
            };
            state.alerts.push(newAlert);
        },
        dismissAlert: (state, action) => {
            const removed = state.alerts.find((alert) => alert.id === action.payload);
            if (removed?.type === 'sos_emergency' && removed.deviceId) {
                if (!state.dismissedSosDeviceIds.includes(removed.deviceId)) {
                    state.dismissedSosDeviceIds.push(removed.deviceId);
                }
            }
            state.alerts = state.alerts.filter(alert => alert.id !== action.payload);
        },
        dismissAllAlerts: (state) => {
            state.alerts.forEach((alert) => {
                if (alert.type === 'sos_emergency' && alert.deviceId
                    && !state.dismissedSosDeviceIds.includes(alert.deviceId)) {
                    state.dismissedSosDeviceIds.push(alert.deviceId);
                }
            });
            state.alerts = [];
        },
        clearDismissedSosDevice: (state, action) => {
            const deviceId = action.payload;
            if (!deviceId) return;
            state.dismissedSosDeviceIds = state.dismissedSosDeviceIds.filter((id) => id !== deviceId);
        },
        setSelectedBeachGeofence: (state, action) => {
            state.selectedBeachGeofence = action.payload;
        },
        clearSelectedBeachGeofence: (state) => {
            state.selectedBeachGeofence = null;
        },
        setLoading: (state, action) => {
            state.loading = action.payload;
        },
        setShouldNavigateToSelectedDevice: (state, action) => {
            state.shouldNavigateToSelectedDevice = action.payload;
        },
        setSelectedCity: (state, action) => {
            state.selectedCity = action.payload;
            // Clear boundary when city changes (will be fetched when needed)
            if (!action.payload) {
                state.cityBoundary = null;
            }
        },
        setSelectedState: (state, action) => {
            state.selectedState = action.payload;
            // Clear boundary when state changes (will be fetched when needed)
            if (!action.payload) {
                state.cityBoundary = null;
            }
        },
        setSelectedCountry: (state, action) => {
            state.selectedCountry = action.payload || 'in';
        },
        setCityBoundary: (state, action) => {
            state.cityBoundary = action.payload;
        },
        clearCitySelection: (state) => {
            state.selectedCity = '';
            state.selectedState = '';
            state.cityBoundary = null;
        }
    }
});

export const {
    toggleSidebar,
    setSidebarOpen,
    addAlert,
    dismissAlert,
    dismissAllAlerts,
    clearDismissedSosDevice,
    setSelectedBeachGeofence,
    clearSelectedBeachGeofence,
    setLoading,
    setShouldNavigateToSelectedDevice,
    setSelectedCity,
    setSelectedState,
    setSelectedCountry,
    setCityBoundary,
    clearCitySelection
} = uiSlice.actions;

export default uiSlice.reducer;
